import React, { useEffect } from 'react';
import { CaseStudyTile } from './case-studies';
import { getSettingsClass } from '../../utils/helpers';
import { LayoutPadding } from '~/layouts';
import { graphql } from 'gatsby';
import { useAutoAnimate } from '@formkit/auto-animate/react';

interface CaseStudyProps {
  settings: Settings;
  case_study_panel: CaseStudyPanelItem[];
}

const CaseStudyPanel: React.FC<CaseStudyProps> = ({
  settings,
  case_study_panel,
}) => {
  const [container] = useAutoAnimate<HTMLDivElement>({
    easing: 'ease-out',
  });
  const [isChanging, setIsChanging] = React.useState(false);
  const panelRef = React.useRef<HTMLDivElement>(null);

  if (!case_study_panel) return null;

  const showController = case_study_panel.some(study => study.control_column);
  const settingsClass = getSettingsClass(settings);
  const [activeStudy, setActiveStudy] = React.useState(case_study_panel[0]);

  // preload author images so animations are smooth
  useEffect(() => {
    // I tried adding an intersection observer but it was not working
    const authorImages = case_study_panel.map(study => study.author.image);
    authorImages.forEach(src => {
      const image = new Image();
      image.src = src;
    });
  }, [case_study_panel]);

  if (!activeStudy) return null;
  const { author } = activeStudy;

  const handleControllerClick = (caseStudy: CaseStudyPanelItem) => () => {
    setIsChanging(true);
    setTimeout(() => {
      setActiveStudy(caseStudy);
      setIsChanging(false);
    }, 500); // duration should match the CSS transition duration
  };

  return (
    <>
      <LayoutPadding>
        <div
          ref={panelRef}
          className={`block__case-study-panel block ${settingsClass}`}
          style={
            activeStudy.hide_tile
              ? {
                  maxWidth: '84rem',
                }
              : {}
          }
        >
          <div
            ref={container}
            className={`case-study ${activeStudy.hide_tile ? 'case-study-without-tile' : ''} ${isChanging ? 'fading-out' : 'fading-in'}`}
          >
            <div className='case-study__content'>
              <h3 className='case-study__title'>{activeStudy.title}</h3>
              <p className='case-study__description'>
                {activeStudy.description}
              </p>
            </div>
            <div className='case-study__author'>
              <img
                className='author-image'
                src={author.image}
                alt={author.name}
              />
              <div className='case-study__credit'>
                <h4 className='author-name'>{author.name}</h4>
                <p className='author-details'>{author.position}</p>
              </div>
            </div>
          </div>
          {activeStudy.case_study_tile && !activeStudy.hide_tile && (
            <CaseStudyTile
              item={activeStudy.case_study_tile}
              theme='single'
              navigationTrigger='cta'
            />
          )}
        </div>
      </LayoutPadding>
      {showController && (
        <div className={`case-controller`}>
          {case_study_panel.map((caseStudy, index) => {
            if (!caseStudy.control_column) return null;
            const { text, logo } = caseStudy.control_column;
            return (
              <div
                key={`case-controller-${index}`}
                className={`case-controller__item`}
                onClick={handleControllerClick(caseStudy)}
              >
                <div
                  className={`text-with-logo scale-on-hover`}
                  onClick={handleControllerClick(caseStudy)}
                >
                  {text && (
                    <div
                      className='text-with-logo__text'
                      dangerouslySetInnerHTML={{ __html: text }}
                    />
                  )}
                  {logo && (
                    <img
                      src={logo}
                      alt='Company logo'
                      className='text-with-logo__logo'
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default CaseStudyPanel;

export const caseStudyPanelFragment = graphql`
  fragment CaseStudyPanelFragment on Block {
    case_study_panel {
      title
      description
      author {
        name
        position
        image
      }
      control_column {
        text
        logo
      }
      hide_tile
      case_study_tile {
        title
        title_short
        cta_text
        cta_url
        open_in_new_tab
        logo {
          title
          icon
        }
        cover {
          image
          image_alt_text
          horizontal_position
          offset {
            horizontal
            vertical
          }
        }
        background {
          degrees
          from
          to
        }
      }
    }
  }
`;
